import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, QueryStateBoundary } from "@/components/ui";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useCalendarFeedUrl, useCalendarMonth } from "@/api/hooks/useCalendar";
import { EVENT_STATUS_BADGE_VARIANT, EVENT_STATUS_LABELS } from "@/api/types/event";
import type { CalendarEvent, CalendarHold } from "@/api/types/calendar";
import {
  currentMonth,
  formatMonthLabel,
  getMonthGridDays,
  shiftMonth,
} from "@/features/events/eventDate";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Builder 4's calendar — a month grid of confirmed events PLUS ACTIVE
 * holds (a sent-but-not-yet-accepted quote provisionally occupying a
 * date), so a booking coordinator can see "tentatively spoken for" dates
 * alongside firm bookings. Distinct from features/events/EventCalendarPage
 * (events only, no hold awareness).
 */
export function CalendarPage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(currentMonth());
  const [year, monthNum] = useMemo(() => month.split("-").map(Number), [month]);

  const { data, isLoading, error, refetch } = useCalendarMonth(year, monthNum);
  const feedUrlMutation = useCalendarFeedUrl();

  const days = useMemo(() => getMonthGridDays(month), [month]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of data?.events ?? []) {
      const list = map.get(event.event_date) ?? [];
      list.push(event);
      map.set(event.event_date, list);
    }
    return map;
  }, [data]);

  const holdsByDay = useMemo(() => {
    const map = new Map<string, CalendarHold[]>();
    for (const hold of data?.holds ?? []) {
      const list = map.get(hold.event_date) ?? [];
      list.push(hold);
      map.set(hold.event_date, list);
    }
    return map;
  }, [data]);

  async function handleCopyFeedUrl() {
    try {
      const result = await feedUrlMutation.mutateAsync();
      await navigator.clipboard.writeText(result.url);
      toastSuccess("Calendar feed URL copied", "Paste it into Google/Apple/Outlook Calendar's “subscribe” field.");
    } catch {
      toastError("Couldn't copy the calendar feed URL", "Try again, or copy it manually from the network response.");
    }
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Calendar</h1>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => void handleCopyFeedUrl()}
            disabled={feedUrlMutation.isPending}
            data-testid="copy-calendar-feed-url"
          >
            {feedUrlMutation.isPending ? "Minting link…" : "Copy calendar feed URL"}
          </Button>
          <Button variant="secondary" onClick={() => navigate("/events")}>
            Events list
          </Button>
        </div>
      </div>

      <div className="mb-4 flex items-center justify-center gap-4">
        <Button size="sm" variant="secondary" onClick={() => setMonth((m) => shiftMonth(m, -1))} aria-label="Previous month">
          ← Prev
        </Button>
        <h2 className="min-w-[10rem] text-center text-lg font-semibold text-text-primary" data-testid="calendar-month-label">
          {formatMonthLabel(month)}
        </h2>
        <Button size="sm" variant="secondary" onClick={() => setMonth((m) => shiftMonth(m, 1))} aria-label="Next month">
          Next →
        </Button>
      </div>

      <div className="mb-4 flex items-center justify-center gap-4 text-xs text-text-muted">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-primary" /> Booked event
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full border border-warning bg-transparent" /> Held (quote sent, not yet accepted)
        </span>
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        <div className="overflow-x-auto">
          <div className="grid min-w-[640px] grid-cols-7 gap-px rounded-lg border border-border bg-border">
            {WEEKDAY_LABELS.map((label) => (
              <div key={label} className="bg-bg-hover px-2 py-1 text-center text-xs font-semibold uppercase text-text-muted">
                {label}
              </div>
            ))}
            {days.map((day) => {
              const dayEvents = eventsByDay.get(day.iso) ?? [];
              const dayHolds = holdsByDay.get(day.iso) ?? [];
              return (
                <div
                  key={day.iso}
                  data-day={day.iso}
                  className={`min-h-[6rem] bg-bg-card p-1.5 ${day.inMonth ? "" : "opacity-40"} ${
                    day.isToday ? "ring-2 ring-inset ring-primary" : ""
                  }`}
                >
                  <div className="mb-1 text-xs font-medium text-text-secondary">{day.date.getDate()}</div>
                  <div className="space-y-1">
                    {dayEvents.map((event) => (
                      <button
                        key={event.id}
                        type="button"
                        onClick={() => navigate(`/quotes/${event.quote_id}`)}
                        className="block w-full truncate rounded px-1 py-0.5 text-left text-xs hover:bg-bg-hover"
                        data-event-chip={event.id}
                      >
                        <Badge variant={EVENT_STATUS_BADGE_VARIANT[event.status]} size="sm" className="mr-1">
                          {EVENT_STATUS_LABELS[event.status]}
                        </Badge>
                        {event.venue_name ?? "Event"}
                      </button>
                    ))}
                    {dayHolds.map((hold) => (
                      <button
                        key={hold.id}
                        type="button"
                        onClick={() => navigate(`/quotes/${hold.quote_id}`)}
                        className="block w-full truncate rounded px-1 py-0.5 text-left text-xs hover:bg-bg-hover"
                        data-hold-chip={hold.id}
                      >
                        <Badge variant="warning" size="sm" className="mr-1">
                          Held
                        </Badge>
                        Quote pending
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </QueryStateBoundary>
    </div>
  );
}
